import React, { useCallback, useEffect, useState } from "react";
import Styles from "./NotificationContainer.module.css";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { ReactComponent as UpMark } from "../../../assets/images/UpMark.svg";
import { useSelector } from "react-redux";
import {
  getNotificationCount,
  handleNotifyApi,
  handleNotifyRead,
  markAllRead,
} from "../../../Services/Chat/Chat";
import { ErrorMessage } from "../../Toast/ErrorMessage";
import { formatDateTimeView } from "../../FormatDate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import { ReactComponent as Check } from "../../../assets/images/checkSuccess.svg";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import MoneyOffRoundedIcon from "@mui/icons-material/MoneyOffRounded";
import { ReactComponent as Hold } from "../../../assets/images/warningIcon.svg";

const NotificationContainer = () => {
  const upcoming = useSelector((state) => state?.notificationSlice?.latest);

  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId,
  );

  const [notificationModal, setNotificationModal] = useState(false);
  const [notify, setNotify] = useState([]);
  const [notifyCount, setNotifyCount] = useState(0);

  const handleNotify = useCallback(async () => {
    try {
      let res = await handleNotifyApi({ userId });

      // FIXED typo: lendth -> length
      if (res?.data?.notifications?.length === 0) {
        setNotify([]);
      } else {
        setNotify(res?.data?.notifications || []);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setNotify([]);
    }
  }, [userId]);

  const handleCount = useCallback(async () => {
    try {
      const res = await getNotificationCount({ userId });
      const countValue = res?.data?.unreadCount || 0;
      setNotifyCount(countValue);
    } catch (error) {
      console.error("Failed to fetch notification count:", error);
      setNotifyCount(0);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      handleNotify();
      handleCount();
    }
  }, [userId, upcoming, handleNotify, handleCount]);

  const handleRead = async (id) => {
    try {
      let res = await handleNotifyRead({ id });

      if (res?.data?.message) {
        handleNotify();
        handleCount();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      toast.info("Marking all as read...");
      let res = await markAllRead({ userId });

      setNotify((prevNotifications) =>
        prevNotifications.map((notification) => ({
          ...notification,
          isRead: true,
        })),
      );
      setNotifyCount(0);
      handleCount();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      ErrorMessage("Failed to mark all as read");
      handleCount();
    }
  };

  const getNotificationType = (title = "") => {
    const text = title.toLowerCase();

    if (
      text.includes("created") ||
      text.includes("received") ||
      text.includes("successfully") ||
      text.includes("sucessfully")
    ) {
      return "success";
    }

    if (text.includes("hold")) {
      return "warning";
    }

    if (text.includes("withdraw") || text.includes("insufficient")) {
      return "error";
    }

    if (text.includes("revised")) {
      return "info";
    }

    return "default";
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "success":
        return <Check style={{ width: 12, height: 12 }} />;

      case "warning":
        return <Hold style={{ width: 14, height: 14 }} />;

      case "error":
        return (
          <CancelRoundedIcon
            style={{
              color: "#dc2626",
              fontSize: "18px",
            }}
          />
        );

      case "info":
        return "ℹ️";

      default:
        return "🔔";
    }
  };

  return (
    <div className={Styles.notificationWrapper}>
      <div
        className={Styles.notificationIconContainer}
        onClick={() => setNotificationModal(!notificationModal)}
      >
        {notifyCount > 0 && (
          <div className={Styles.countContianer}>
            {notifyCount > 10 ? "10+" : notifyCount}
          </div>
        )}

        {notifyCount > 0 ? (
          <NotificationsActiveIcon style={{ color: "#b8b5c5ff" }} />
        ) : (
          <NotificationsNoneIcon style={{ color: "#b8b5c5ff" }} />
        )}
      </div>

      {notificationModal && (
        <div
          className={Styles.notificationModal}
          onMouseLeave={() => setNotificationModal(false)}
        >
          <div className={Styles.notificationModalHeader}>
            <div className={Styles.upMarkIcon}>
              <UpMark />
            </div>

            <div className={Styles.headerContent}>
              <span className={Styles.headerTitle}>Notifications</span>

              {notify?.length > 0 && (
                <span
                  className={Styles.markAllRead}
                  onClick={handleMarkAllRead}
                >
                  Mark all as read
                </span>
              )}
            </div>
          </div>

          <div className={`scroll_bar_y ${Styles.notifyContenet}`}>
            {notify?.length > 0 ? (
              notify?.map((ele) => {
                const type = getNotificationType(ele?.title);
                return (
                  <div
                    key={ele?.id}
                    className={`
    ${Styles.singleNotification}
    ${!ele?.isRead ? Styles.unreadNotification : ""}
    ${Styles[`${type}Notification`]}
  `}
                    onMouseEnter={() => !ele?.isRead && handleRead(ele?.id)}
                  >
                    <div className={Styles.notificationTop}>
                      <div className={Styles.notificationIcon}>
                        {getNotificationIcon(type)}
                      </div>

                      <div className={Styles.notificationContent}>
                        <div
                          className={`${Styles.msgHeading} ${Styles[`${type}Title`]}`}
                        >
                          {ele?.title}
                        </div>

                        <div className={Styles.msgDetails}>{ele?.message}</div>

                        <div className={Styles.notificationFooter}>
                          <div className={Styles.msgDate}>
                            {formatDateTimeView(ele?.created_at)}
                          </div>

                          {ele?.isRead && (
                            <CheckCircleIcon
                              style={{
                                fontSize: "14px",
                                color: "green",
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={Styles.emptyNotification}>
                <div className={Styles.emptyIcon}>🔔</div>
                <div>No notifications yet</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationContainer;
